import {
  ReactSketchCanvas,
  type ReactSketchCanvasRef,
} from "react-sketch-canvas";
import { type ChangeEvent, forwardRef, useEffect, useRef, useState } from "react";
import { Eraser, MoveVertical, ChevronsDownUp, Pen, RefreshCcw, ScanText, Loader2 } from "lucide-react";
import './css/Button.css'
import { useTranslation } from "react-i18next";

const iconButton =
  "size-9 [@media(max-height:500px)]:size-7! flex items-center justify-center rounded-[10px] cursor-pointer dark:border-accent-900 dark:text-accent-200";
const defaultIconButton =
  "bg-transparent text-accent-900 hover:bg-primary hover:text-white dark:hover:bg-accent-800";
// 手机横屏(≤500)下工具图标缩小:CSS width/height 覆盖各 lucide 图标的 size prop(桌面无此媒体查询,仍用 prop 原值)
const landscapeIconSize = "[@media(max-height:500px)]:size-4!";

// 手机横屏(高≤500)判定:仅横屏才给写字板套「点按书写」蒙层(解决与 277px 面板滚动的手势冲突);
// 桌面/平板直接书写、不套蒙层。跟随旋转变化。
function useIsMobileLandscape() {
  const [is, setIs] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-height: 500px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-height: 500px)");
    const on = () => setIs(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return is;
}

interface AnswerCanvasProps {
  canvasId: string;
  onOcrRecognize?: () => void;
  isOcrProcessing?: boolean;
}

export const AnswerCanvas = forwardRef<ReactSketchCanvasRef, AnswerCanvasProps>((prop, ref) => {
  const [eraser, setEraser] = useState(false);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const { t } = useTranslation();

  const isMobileLandscape = useIsMobileLandscape();
  // 书写区基础高度按屏况「实时」决定,不在挂载时用 useRef/useState 冻结 —— 否则竖屏(或 >500 时)挂载后再进横屏,
  // 画布高会停在 200/260 不缩,横屏就被「撑高」。横屏(≤500)空间紧张取紧凑 72px(约两行手写,右下「完成」pill 仍在
  // 右上工具栏下方不相撞);矮屏 200 / 高屏 260。touch-action:none 吞滑动手势的问题已由下方「点按书写」蒙层放行滚动解决。
  const baseHeight = isMobileLandscape ? 72 : window.innerHeight >= 1000 ? 260 : 200;
  // ↕ 加长累计的增量(相对基础高),合拢按 60 递减到 0;基础高随横竖屏切换变化时,总高自动跟随。
  const [expandDelta, setExpandDelta] = useState(0);
  const height = baseHeight + expandDelta;

  // 「点按书写」蒙层态:armed=true 显示蒙层(手指拖=滚动面板 / 点按=进书写);false 为书写态(画布接管)。仅手机横屏生效。
  const [armed, setArmed] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);

  // 书写态下点画布以外任意处 → 复位蒙层(恢复滚动);切页签/切到别的题也经此自动复位。
  useEffect(() => {
    if (!isMobileLandscape || armed) return;
    const onDown = (e: PointerEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) setArmed(true);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [isMobileLandscape, armed]);

  const handleEraserClick = () => {
    setEraser(true);
    if (ref?.current) {
      ref.current.eraseMode(true);
    }
  };

  const handlePencilClick = () => {
    setEraser(false);
    if (ref.current) {
      ref.current.eraseMode(false);
    }
  };

  const handleResetClick = () => {
    if (ref.current) {
      ref.current.resetCanvas();
    }
  };

  const handleExpandClick = () => setExpandDelta((d) => d + 60);

  // 缩短: 与加长同步长(60px),下限=基础高度(delta 到 0)——只撤销之前的加长,基础高时不再变矮
  const handleShrinkClick = () => setExpandDelta((d) => Math.max(0, d - 60));

  const pencilSelected = !eraser
    ? "bg-[#9CEA4E] text-black hover:bg-[#9CEA4E] hover:text-black"
    : defaultIconButton;

  const eraserSelected = eraser
    ? "bg-[#9CEA4E] text-black hover:bg-[#9CEA4E] hover:text-black"
    : defaultIconButton;

  const onColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStrokeColor(event.target.value);
  };

  return (
    <div className="reset-wrapper flex flex-col flex-1">
      <div ref={cardRef} className="relative rounded-[15px] overflow-hidden">
        {/* 工具栏 - absolute 右上,与画布共享同一个虚线框
            手机横屏(≤500):图标与按钮方格改小尺寸(真实尺寸,非 scale);桌面/平板(height>500)不触发,样式不动
            z-20:压在「点按书写」蒙层(z-10)之上,横屏未进书写态时工具栏仍可点(改色/笔/加长/OCR) */}
        <div className="absolute right-0.5 top-0.5 z-20 flex flex-row gap-0.5 items-center">
          {/* 色块占与图标按钮同宽的 36px 方格;mr 补偿选中笔绿底块的实心填充,避免与色块挤一起 */}
          <div className="size-9 [@media(max-height:500px)]:size-7! flex items-center justify-center mr-1 [@media(max-height:500px)]:mr-0.5!">
            <div className="w-[26px] h-[26px] [@media(max-height:500px)]:w-5! [@media(max-height:500px)]:h-5! rounded-full overflow-hidden">
              <input
                title="Color"
                className="w-[200%] h-[200%] bg-transparent border-none cursor-pointer appearance-none transform-cpu -translate-x-1/4 -translate-y-1/4"
                type="color"
                value={strokeColor}
                onChange={onColorChange}
              />
            </div>
          </div>
          <button
            title="Pencil"
            className={`${iconButton} ${pencilSelected} border-none`}
            type="button"
            aria-label="pencil"
            onClick={handlePencilClick}
          >
            <Pen size={20} className={landscapeIconSize} />
          </button>
          <button
            title="Eraser"
            className={`${iconButton} ${eraserSelected} border-none`}
            type="button"
            aria-label="eraser"
            onClick={handleEraserClick}
          >
            <Eraser size={22} className={landscapeIconSize} />
          </button>
          {/* ↕ 加长(原图标) + 双箭头合拢=缩短;两者笔画都窄,收窄此格宽度(w-7)抵消左右多余留白,使间隙与其它图标一致 */}
          <button
            title={t('component.expand')}
            className={`${iconButton} ${defaultIconButton} relative border-none w-7! [@media(max-height:500px)]:w-[22px]!`}
            type="button"
            aria-label="expand"
            onClick={handleExpandClick}
          >
            <MoveVertical size={22} className={landscapeIconSize} />
          </button>
          <button
            title={t('component.shrink')}
            className={`${iconButton} ${defaultIconButton} relative border-none w-7! [@media(max-height:500px)]:w-[22px]!`}
            type="button"
            aria-label="shrink"
            onClick={handleShrinkClick}
          >
            <ChevronsDownUp size={22} className={landscapeIconSize} />
          </button>
          <button
            title={t('component.reset')}
            className={`${iconButton} ${defaultIconButton} relative border-none`}
            type="button"
            aria-label="clear"
            onClick={handleResetClick}
          >
            <RefreshCcw size={22} className={landscapeIconSize} />
          </button>
          <button
            title={t('component.convertOCR', { defaultValue: t('component.ocrRecognize', { defaultValue: 'OCR识别' }) })}
            className={`${iconButton} ${defaultIconButton} relative flex items-center`}
            type="button"
            aria-label="ocr-recognize"
            onClick={prop.onOcrRecognize}
            disabled={prop.isOcrProcessing}
          >
            {prop.isOcrProcessing ? <Loader2 className={`animate-spin ${landscapeIconSize}`} size={22} /> : <ScanText size={22} className={landscapeIconSize} />}
          </button>
        </div>
        <ReactSketchCanvas
          id={prop.canvasId}
          ref={ref}
          canvasColor="transparent"
          height={`${height}px`}
          strokeWidth={4}
          eraserWidth={40}
          strokeColor={strokeColor}
          withTimestamp={true}
          aria-label={t('quiz.handwritingArea', { defaultValue: '手寫答案區' })}
          // 关掉 ReactSketchCanvas 默认自带的 1px 灰边框,
          // 否则画布外会出现一圈实线,与外层白卡的虚线框冲突。
          style={{ border: "none" }}
        />
        {/* 手机横屏 · 未进书写态:盖一层蒙层。手指拖动照常滚动 277px 面板(蒙层用默认 touch-action、不吞手势),
            点按才隐藏蒙层进入书写。桌面/平板不渲染,画布直接可写。已有笔迹从近透明蒙层透出可见。 */}
        {isMobileLandscape && armed && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            onClick={() => setArmed(false)}
          >
            <span aria-live="polite" className="sr-only">
              {t('quiz.tapToWriteHint', { defaultValue: '點按進入手寫模式' })}
            </span>
            <span className="pointer-events-none flex items-center gap-1 rounded-full bg-white/85 px-3 py-1 text-xs font-medium text-[#006609] shadow-sm backdrop-blur-[1px]">
              <Pen size={14} />
              {t('component.tapToWrite', { defaultValue: '点按书写' })}
            </span>
          </div>
        )}
        {/* 手机横屏 · 书写态:画布右下角浮「完成写字」pill,点它复位蒙层、恢复滚动
            (z-20 高于画布,书写时也点得中);另可点画布以外任意处复位(见上方 effect)。 */}
        {isMobileLandscape && !armed && (
          <button
            type="button"
            aria-label="done-writing"
            onClick={() => setArmed(true)}
            className="absolute bottom-2 right-2 z-20 flex items-center gap-1 rounded-full bg-[#9CEA4E] px-3 py-1 text-xs font-medium text-black shadow-sm active:brightness-95"
          >
            {t('component.doneWriting', { defaultValue: '完成' })}
          </button>
        )}
      </div>
    </div>
  );
})